import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import DarkModeIcon from '@mui/icons-material/DarkMode'
import LightModeIcon from '@mui/icons-material/LightMode'
import LogoutIcon from '@mui/icons-material/Logout'
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'
import ably from '../ablyClient'
import MainDashboardMap from '../components/MainDashboardMap'
import MainDashboardSidebar from '../components/MainDashboardSidebar'
import {
  getBoardInfo,
  clearLoginSession,
  getLoginSession,
  getProfileDeviceIds,
  getUserDisplayInfo,
  getUserProfile,
} from '../services/authApi'
import {
  getNotificationPermission,
  getPowerNotificationState,
  requestNotificationPermission,
  showPowerNotification,
} from '../services/pwaNotifications'
import {
  getOneSignalPermission,
  initOneSignalUser,
  isOneSignalConfigured,
  requestOneSignalPermission,
} from '../services/oneSignalNotifications'
import './MainDashboardPage.css'

const ABLY_CHANNEL = process.env.REACT_APP_ABLY_CHANNEL || 'alarms'
const ABLY_EVENT = process.env.REACT_APP_ABLY_EVENT || 'alarm.triggered'

function getInitialNotificationPermission() {
  return isOneSignalConfigured() ? getOneSignalPermission() : getNotificationPermission()
}

function getPermissionValue(permissionResult) {
  return typeof permissionResult === 'string' ? permissionResult : permissionResult?.permission || 'unsupported'
}

function getInitialDashboardUser() {
  const loginSession = getLoginSession()

  return getUserDisplayInfo(loginSession?.user)
}

function unwrapBoardInfo(boardInfo) {
  if (!boardInfo || typeof boardInfo !== 'object') {
    return {}
  }

  if (boardInfo.success !== undefined && boardInfo.data && typeof boardInfo.data === 'object') {
    return boardInfo.data
  }

  return boardInfo
}

function getPayloadDeviceId(payload) {
  const board = unwrapBoardInfo(payload)

  return board?.config?.Device?.ID || board?.data?.DeviceID || board?.data?.deviceId || board?.id
}

function getBoardStatusCode(payload) {
  const board = unwrapBoardInfo(payload)

  return board?.ml?.status ?? board?.data?.ml?.status ?? board?.devicedata?.ml?.status
}

function createBoardInfoFallback(deviceId) {
  return {
    config: {
      Device: {
        ID: deviceId,
      },
    },
    data: {
      deviceId,
    },
  }
}

async function loadBoardInfo(deviceId) {
  try {
    return await getBoardInfo(deviceId)
  } catch (error) {
    console.warn(`Unable to fetch board info for ${deviceId}:`, error)

    return createBoardInfoFallback(deviceId)
  }
}

function getAblyBoardPayload(messageData, deviceId, allowUnscopedPayload = false) {
  if (!messageData || typeof messageData !== 'object') {
    return null
  }

  if (messageData[deviceId]) {
    return messageData[deviceId]
  }

  if (messageData.data?.[deviceId]) {
    return messageData.data[deviceId]
  }

  const payloadDeviceId = getPayloadDeviceId(messageData)

  if (payloadDeviceId === deviceId || (allowUnscopedPayload && !payloadDeviceId)) {
    return messageData
  }

  return null
}

function replaceBoardInfo(boardInfos, deviceId, updatePayload) {
  const hasExistingBoard = boardInfos.some((boardInfo) => getPayloadDeviceId(boardInfo) === deviceId)

  if (!hasExistingBoard) {
    return [...boardInfos, mergeBoardInfo(createBoardInfoFallback(deviceId), updatePayload)]
  }

  return boardInfos.map((boardInfo) => {
    return getPayloadDeviceId(boardInfo) === deviceId ? mergeBoardInfo(boardInfo, updatePayload) : boardInfo
  })
}

function mergeBoardInfo(currentBoardInfo, updatePayload) {
  const current = unwrapBoardInfo(currentBoardInfo)
  const update = unwrapBoardInfo(updatePayload)

  if (!Object.keys(update).length) {
    return currentBoardInfo
  }

  const merged = {
    ...current,
    ...update,
    config: update.config ?? current.config,
    control: update.control ?? current.control,
    data: update.data ?? current.data,
    devicedata: update.devicedata ?? current.devicedata,
    ml: update.ml ?? current.ml,
    response: update.response ?? current.response,
  }

  return currentBoardInfo?.success !== undefined ? { ...currentBoardInfo, data: merged } : merged
}

function MainDashboardPage({ isDarkMode = false, onSelectTheme }) {
  const [boardInfo, setBoardInfo] = useState(null)
  const [boardInfos, setBoardInfos] = useState([])
  const [deviceId, setDeviceId] = useState('')
  const [deviceIds, setDeviceIds] = useState([])
  const [dashboardUser, setDashboardUser] = useState(getInitialDashboardUser)
  const [notificationPermission, setNotificationPermission] = useState(getInitialNotificationPermission)
  const [showSupplierAlert, setShowSupplierAlert] = useState(false)
  const lastNotificationLevelRef = useRef('')
  const notificationContextRef = useRef({})
  const statusOneBoards = useMemo(() => {
    return boardInfos.filter((boardInfoEntry) => {
      return String(getBoardStatusCode(boardInfoEntry)) === '1'
    })
  }, [boardInfos])
  const hasStatusOneBoard = statusOneBoards.length > 0

  const maybeNotifyPowerStatus = useCallback((nextBoardInfo, permission = notificationPermission) => {
    const notificationState = getPowerNotificationState(nextBoardInfo)

    if (!notificationState || notificationState.level === lastNotificationLevelRef.current || permission !== 'granted') {
      return
    }

    if (isOneSignalConfigured()) {
      lastNotificationLevelRef.current = notificationState.level
      return
    }

    showPowerNotification(notificationState).then((shown) => {
      if (shown) {
        lastNotificationLevelRef.current = notificationState.level
      }
    })
  }, [notificationPermission])

  const handleEnableNotifications = async () => {
    const permissionResult = isOneSignalConfigured()
      ? await requestOneSignalPermission(notificationContextRef.current)
      : await requestNotificationPermission()
    const nextPermission = getPermissionValue(permissionResult)

    setNotificationPermission(nextPermission)

    if (nextPermission === 'granted') {
      maybeNotifyPowerStatus(boardInfo, nextPermission)
    }
  }

  useEffect(() => {
    maybeNotifyPowerStatus(boardInfo)
  }, [boardInfo, maybeNotifyPowerStatus])

  useEffect(() => {
    if (hasStatusOneBoard) {
      setShowSupplierAlert(true)
    }
  }, [hasStatusOneBoard])

  useEffect(() => {
    if (!showSupplierAlert || !hasStatusOneBoard) {
      return undefined
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setShowSupplierAlert(false)
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [hasStatusOneBoard, showSupplierAlert])

  useEffect(() => {
    let isMounted = true
    const loginSession = getLoginSession()

    if (loginSession?.user) {
      setDashboardUser(getUserDisplayInfo(loginSession.user))
    }

    if (!loginSession?.user?.email) {
      return
    }

    getUserProfile(loginSession.user.email)
      .then((profileResponse) => {
        const profileDeviceIds = getProfileDeviceIds(profileResponse)
        const selectedDeviceId = profileDeviceIds[0] || ''
        const profileUser = profileResponse?.user || {}
        const sessionUser = loginSession.user || {}

        if (isMounted) {
          setDashboardUser(getUserDisplayInfo({ ...sessionUser, ...profileUser }))
        }

        if (!selectedDeviceId) {
          console.warn('No deviceId found in profile response:', profileResponse)
          if (isMounted) {
            setDeviceId('')
            setDeviceIds([])
            setBoardInfo(null)
            setBoardInfos([])
          }
          return []
        }

        if (isMounted) {
          setDeviceId(selectedDeviceId)
          setDeviceIds(profileDeviceIds)
        }

        const notificationContext = {
          deviceId: selectedDeviceId,
          email: profileUser.email || sessionUser.email,
          externalId: selectedDeviceId,
          userId: profileUser.userId || sessionUser.userId,
        }

        notificationContextRef.current = notificationContext

        initOneSignalUser(notificationContext)
          .then((result) => {
            if (isMounted) {
              setNotificationPermission(result.permission)
            }
          })
          .catch((error) => {
            console.error('OneSignal registration failed:', error)
          })

        return Promise.all(profileDeviceIds.map(loadBoardInfo))
      })
      .then((boardInfoResponses) => {
        if (boardInfoResponses?.length) {
          if (isMounted) {
            setBoardInfos(boardInfoResponses)
            setBoardInfo(boardInfoResponses[0])
          }
        }
      })
      .catch((error) => {
        console.error('Dashboard data request failed:', error)
      })

    return () => {
      isMounted = false
    }
  }, [])

  useEffect(() => {
    if (!deviceIds.length) {
      return undefined
    }

    const channel = ably.channels.get(ABLY_CHANNEL)
    const handler = (message) => {
      const update = deviceIds.reduce((matchedUpdate, currentDeviceId) => {
        if (matchedUpdate) {
          return matchedUpdate
        }

        const updatePayload = getAblyBoardPayload(message.data, currentDeviceId, deviceIds.length === 1)

        return updatePayload ? { deviceId: currentDeviceId, updatePayload } : null
      }, null)

      if (!update) {
        return
      }

      if (String(getBoardStatusCode(update.updatePayload)) === '1') {
        setShowSupplierAlert(true)
      }

      setBoardInfo((currentBoardInfo) => {
        return update.deviceId === deviceId
          ? mergeBoardInfo(currentBoardInfo || createBoardInfoFallback(update.deviceId), update.updatePayload)
          : currentBoardInfo
      })
      setBoardInfos((currentBoardInfos) => {
        return replaceBoardInfo(currentBoardInfos, update.deviceId, update.updatePayload)
      })
    }

    channel.subscribe(ABLY_EVENT, handler)

    return () => {
      channel.unsubscribe(ABLY_EVENT, handler)
    }
  }, [deviceId, deviceIds])

  return (
    <main className="dashboard-page">
      <section className="dashboard-shell" aria-label="CNERGI energy dashboard">
        <MainDashboardSidebar />

        <div className="dashboard-main">
          <header className="dashboard-header">
            <div className="dashboard-header__welcome">
              <span>CNERGI</span>
              <h1>Welcome, {dashboardUser.firstName}!</h1>
              <p>{dashboardUser.email}</p>
            </div>

            <div className="dashboard-header__actions">
              <button
                className="dashboard-theme-toggle"
                type="button"
                aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                onClick={() => onSelectTheme?.(isDarkMode ? 'light' : 'dark')}
              >
                {isDarkMode ? <LightModeIcon aria-hidden="true" /> : <DarkModeIcon aria-hidden="true" />}
              </button>

              <button
                className="dashboard-theme-toggle"
                type="button"
                aria-label="Enable PWA notifications"
                title={`Notifications: ${notificationPermission}`}
                onClick={handleEnableNotifications}
              >
                <NotificationsActiveIcon aria-hidden="true" />
              </button>

              <div className="dashboard-user">
                <AccountCircleIcon aria-hidden="true" />
                <strong>{dashboardUser.displayName}</strong>
              </div>

              <a className="dashboard-logout" href="/login" onClick={clearLoginSession}>
                <LogoutIcon aria-hidden="true" />
                Logout
              </a>
            </div>
          </header>

          <MainDashboardMap boardInfo={boardInfo} boardInfos={boardInfos} />
        </div>
      </section>

      {showSupplierAlert && hasStatusOneBoard ? (
        <div className="dashboard-status-alert__overlay" role="presentation">
          <section
            className="dashboard-status-alert"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="dashboard-status-alert-title"
            aria-describedby="dashboard-status-alert-description"
          >
            <div className="dashboard-status-alert__badge" aria-hidden="true">!</div>
            <p className="dashboard-status-alert__eyebrow">Device status alert</p>
            <h2 id="dashboard-status-alert-title">Supplier assistance required</h2>
            <p id="dashboard-status-alert-description">
              Status code 1 was reported. Please contact your supplier immediately for assistance.
            </p>

            <div className="dashboard-status-alert__devices" aria-label="Affected devices">
              {statusOneBoards.map((boardInfoEntry, index) => {
                const affectedDeviceId = getPayloadDeviceId(boardInfoEntry) || `Device ${index + 1}`

                return (
                  <div className="dashboard-status-alert__device" key={affectedDeviceId}>
                    <i aria-hidden="true"></i>
                    <span>{affectedDeviceId}</span>
                  </div>
                )
              })}
            </div>

            <button
              className="dashboard-status-alert__button"
              type="button"
              autoFocus
              onClick={() => setShowSupplierAlert(false)}
            >
              I understand
            </button>
          </section>
        </div>
      ) : null}
    </main>
  )
}

export default MainDashboardPage
